import React, { useRef } from 'react'
import {
  SimpleForm, TextInput, SelectInput, BooleanInput, NumberInput,
  ReferenceInput, AutocompleteInput, ReferenceArrayInput, CheckboxGroupInput,
  useNotify, useRedirect,
} from 'react-admin'
import { Box, Typography } from '@mui/material'
import { Create as CreateWrapper } from '../common/views'
import { leadStatusChoices, leadSourceChoices } from './constants'
import { industryChoices } from '../common/constants/industryChoices'
import { addLeadToCampaign } from '../../services/api'

const LeadCreate = (props) => {
  const campaignIdsRef = useRef([])
  const notify = useNotify()
  const redirect = useRedirect()

  const transform = (data) => {
    const { campaign_ids, ...rest } = data
    campaignIdsRef.current = campaign_ids || []
    return rest
  }

  const handleSuccess = async (lead) => {
    const ids = campaignIdsRef.current
    if (ids.length > 0) {
      await Promise.all(ids.map(id => addLeadToCampaign(id, lead.id)))
    }
    notify('ra.notification.created', { messageArgs: { smart_count: 1 } })
    redirect('list', 'leads')
  }

  return (
    <CreateWrapper {...props} transform={transform} mutationOptions={{ onSuccess: handleSuccess }}>
      <SimpleForm>
        {/* Lead Status */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, gridColumn: '1 / -1' }}>
          <SelectInput
            source="status"
            label="Status"
            choices={leadStatusChoices}
            defaultValue="new"
            fullWidth
          />
          <SelectInput
            source="source"
            label="Source"
            choices={leadSourceChoices}
            defaultValue="user"
            fullWidth
          />
        </Box>

        {/* Contact Information */}
        <Typography variant="h6" sx={{ gridColumn: '1 / -1', mt: 2 }}>
          Contact Information
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, gridColumn: '1 / -1' }}>
          <TextInput
            source="first_name"
            label="First Name"
            fullWidth
            validate={(value) => (!value ? 'First name is required' : undefined)}
          />
          <TextInput
            source="last_name"
            label="Last Name"
            fullWidth
            validate={(value) => (!value ? 'Last name is required' : undefined)}
          />
          <TextInput
            source="email"
            label="Email"
            fullWidth
            type="email"
            validate={(value) => (!value ? 'Email is required' : undefined)}
          />
          <TextInput source="website" label="Website" fullWidth />
          <TextInput source="phone" label="Phone" fullWidth />
          <TextInput source="mobile" label="Mobile" fullWidth />
          <TextInput source="company" label="Company" fullWidth />
          <TextInput source="job_title" label="Job Title" fullWidth />
          <TextInput source="department" label="Department" fullWidth />
          <TextInput source="linkedin_url" label="LinkedIn Profile URL" fullWidth />
        </Box>

        {/* Lead Details */}
        <Typography variant="h6" sx={{ gridColumn: '1 / -1', mt: 3 }}>
          Lead Details
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, gridColumn: '1 / -1' }}>
          <TextInput source="campaign" label="Campaign" fullWidth />
          <ReferenceInput
            source="assigned_to_id"
            reference="users"
            label="Assigned To"
            fullWidth
          >
            <AutocompleteInput
              optionText="fullname"
              optionValue="id"
              shouldRenderSuggestions={(val) => true}
              emptyText="Select a user..."
              isLoading={false}
              filterToQuery={(searchText) => ({ q: searchText })}
            />
          </ReferenceInput>
          <SelectInput
            source="language"
            label="Language"
            choices={[
              { id: 'FR', name: 'French' },
              { id: 'EN', name: 'English' },
            ]}
            defaultValue="FR"
            fullWidth
          />
          <SelectInput
            source="industry"
            label="Industry"
            choices={industryChoices}
            fullWidth
          />
          <BooleanInput source="marketing_consent" label="Marketing Consent" defaultValue={true} />
        </Box>

        {/* Campaigns */}
        <Typography variant="h6" sx={{ gridColumn: '1 / -1', mt: 3 }}>
          Campaigns
        </Typography>
        <Box sx={{ gridColumn: '1 / -1' }}>
          <ReferenceArrayInput source="campaign_ids" reference="campaigns" perPage={100}>
            <CheckboxGroupInput optionText="name" row={true} />
          </ReferenceArrayInput>
        </Box>

        {/* Project Information */}
        <Typography variant="h6" sx={{ gridColumn: '1 / -1', mt: 3 }}>
          Project Information
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, gridColumn: '1 / -1' }}>
          <TextInput
            source="project_description"
            label="Project Description (What)"
            multiline
            fullWidth
            rows="3"
          />
          <TextInput
            source="project_reason"
            label="Project Reason (Why)"
            multiline
            fullWidth
            rows="3"
          />
          <TextInput
            source="project_location"
            label="Project Location (Where)"
            fullWidth
          />
          <TextInput
            source="project_timeline"
            label="Project Timeline (When)"
            multiline
            fullWidth
            rows="2"
          />
          <TextInput
            source="project_budget"
            label="Project Budget"
            multiline
            fullWidth
            rows="2"
          />
        </Box>

        {/* Financial */}
        <Typography variant="h6" sx={{ gridColumn: '1 / -1', mt: 3 }}>
          Financial
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, gridColumn: '1 / -1' }}>
          <NumberInput source="expected_revenue" label="Expected Revenue" fullWidth />
        </Box>
      </SimpleForm>
    </CreateWrapper>
  )
}

export default LeadCreate
